import { useAddToCart } from '@/composables/UseAddToCart';
import { useMediaclipCommunication } from '@/composables/UseMediaclipCommunication';
import { ConfiguratorStep } from '@/types/Configurator/ConfiguratorStep';
import { MediaclipProductType } from '@/types/Mediaclip/MediaclipProductType';
import { defineStore } from 'pinia';
import { nextTick } from 'vue';
import { useExtraOptionsStore } from '../ExtraOptionsStore';
import { useFilterStore } from './FilterStore';
import { useMediaclipStore } from './MediaclipStore';
import { useSizeStore } from './SizeStore';
import { usePaazlStore } from '@/stores/Paazl/PaazlStore.ts';

// @ts-ignore
export const useConfiguratorStore = defineStore(
  `configurator-${window.product.sku}`,
  {
    state: () => ({
      product: window.product,
      currentStep: ConfiguratorStep.ProductPage as ConfiguratorStep,
      mediaclipToken: undefined as string | undefined,
      mediaclipProjectId: undefined as string | undefined,
      mediaclipInitialized: false,
      mediaclipLaunched: false,
      // TODO: Placeholder image
      mediaclipPreviewUrl: '',
      mediaclipUserText: window.configurable_product?.mediaclipUserText,
      identifier: undefined as string | undefined,
      isSizeSelectorOpen: false,
      hasSavedSizeSelection: false,
      quoteItemId: undefined as number | undefined,
      isUpdatingProject: true,
      allPicturesUploaded: false,
      hasMediaclipError: false,
      buttonsOnCooldown: false,
      hasSavedAtLeastOneTime: false,
      isEditing: false,
      isMobile: false,
      mediaclipUserInputText: undefined as string | undefined,
      showFullPageLoader: false,
      extraOptionsModalOpen: false,
    }),

    getters: {
      isUploadConfigurator: state =>
        Number(state.product.mediaclip_product_type.valueOf()) ===
        MediaclipProductType.CONFIGURATOR,

      isCollectionProduct: state =>
        Number(state.product.mediaclip_product_type.valueOf()) ===
        MediaclipProductType.COLLECTION,

      isSimpleConfigurator: state =>
        Number(state.product.mediaclip_product_type.valueOf()) ===
        MediaclipProductType.SIMPLE,

      isLetterCollageConfigurator: state =>
        Number(state.product.mediaclip_product_type.valueOf()) ===
        MediaclipProductType.LETTERCOLLAGE,

      isConfiguratorOpen: state =>
        state.currentStep !== ConfiguratorStep.ProductPage,

      configuratorIframe: () => () =>
        document.getElementById('designerFrame') as HTMLIFrameElement,

      fullscreenStates: () => {
        return [
          ConfiguratorStep.Configurator,
          ConfiguratorStep.HalfConfigurator,
          ConfiguratorStep.ExtraOptions,
        ];
      },

      showNextButton: state => {
        return (
          // @ts-ignore TS2339
          state.isUploadConfigurator ||
          // @ts-ignore TS2339
          (state.isSimpleConfigurator && state.allPicturesUploaded) ||
          // @ts-ignore TS2339
          (state.isLetterCollageConfigurator && state.allPicturesUploaded) ||
          // @ts-ignore TS2339
          (state.isSimpleConfigurator &&
            useMediaclipStore().amountOfWorksheets > 0 &&
            useMediaclipStore().filledWorksheets.length <=
              useMediaclipStore().amountOfWorksheets)
        );
      },

      disableNextButton: state => {
        return (
          state.isUpdatingProject ||
          state.hasMediaclipError ||
          // @ts-ignore TS2339
          (state.isSimpleConfigurator &&
            (useMediaclipStore().emptyWorksheets.length > 0 ||
              (useMediaclipStore().filledWorksheets.length !==
                useMediaclipStore().amountOfWorksheets &&
                useMediaclipStore().amountOfWorksheets > 0))) ||
          // @ts-ignore TS2339
          (state.isLetterCollageConfigurator &&
            (useMediaclipStore().emptyWorksheets.length > 0 ||
              (useMediaclipStore().filledWorksheets.length !==
                useMediaclipStore().amountOfWorksheets &&
                useMediaclipStore().amountOfWorksheets > 0)))
        );
      },
    },

    actions: {
      async previousStep(): Promise<void> {
        switch (this.currentStep) {
          case ConfiguratorStep.Configurator:
            if (this.isEditing) {
              window.location.href = window.cart_url;
              this.resetData(false);
              return;
            }
            window.history.back();
            break;
          case ConfiguratorStep.HalfConfigurator:
            if (this.isEditing) {
              window.location.href = window.cart_url;
              this.resetData(false);
              return;
            }
            this.currentStep = ConfiguratorStep.ProductPage;
            await usePaazlStore().requestShippingOptions(
              useSizeStore().paazlDeliveryOptionsRequestObject
            );
            break;
          case ConfiguratorStep.ExtraOptions:
            this.setConfiguratorStep();
            break;
          case ConfiguratorStep.ProductPage:
            // Do nothing, you shouldn't end up here
            console.debug('How did you end up here?');
            break;
        }
      },

      async nextStep(isMobile: boolean): Promise<void> {
        useMediaclipStore().isLoading = true;
        await useMediaclipCommunication().updateProject();

        switch (this.currentStep) {
          case ConfiguratorStep.Configurator:
            await this.handleConfiguratorPage(isMobile);
            break;
          case ConfiguratorStep.HalfConfigurator:
            await this.handleConfiguratorPage(isMobile);
            break;
          case ConfiguratorStep.ProductPage:
            this.setConfiguratorStep();
            break;
          default:
            return;
        }

        this.buttonsOnCooldown = true;
        // Set a 200ms cooldown on buttons to prevent accidental spam-clicking into the cart
        setTimeout(() => {
          this.buttonsOnCooldown = false;
        }, 200);
      },

      async handleConfiguratorPage(isMobile: boolean): Promise<void> {
        if (useExtraOptionsStore().productOptionsCategories.length > 0) {
          await this.goToExtraOptionsPage(isMobile);
          return;
        }

        // Set project thumbnail url
        await useMediaclipCommunication().setProjectThumbnailUrl();

        // Add product directly to the cart
        await useAddToCart().addConfiguratorProductToCart(
          useSizeStore().selectedSizeValue.attributes.sku,
          useSizeStore().selectedSizeValue.attributes.mediaclipProductId
        );
      },

      async goToExtraOptionsPage(isMobile: boolean): Promise<void> {
        if (
          isMobile &&
          !this.hasSavedSizeSelection &&
          this.isUploadConfigurator
        ) {
          this.isSizeSelectorOpen = true;
          return;
        }

        if (this.allPicturesUploaded) {
          await useMediaclipCommunication().setProjectThumbnailUrl();
          this.currentStep = ConfiguratorStep.ExtraOptions;
        }
      },

      setConfiguratorStep(): void {
        switch (Number(this.product.mediaclip_product_type.valueOf())) {
          case MediaclipProductType.CONFIGURATOR.valueOf():
            this.currentStep = ConfiguratorStep.HalfConfigurator;
            break;
          case MediaclipProductType.SIMPLE.valueOf():
            this.currentStep = ConfiguratorStep.Configurator;
            break;
          case MediaclipProductType.LETTERCOLLAGE.valueOf():
            this.currentStep = ConfiguratorStep.Configurator;
            break;
          default:
            console.debug(
              'Unknown mediaclip product type',
              this.product.mediaclip_product_type
            );
            break;
        }
      },

      async closeSizeSelector(positive: boolean): Promise<void> {
        this.isSizeSelectorOpen = false;

        // If user presses "Save" button
        if (positive) {
          this.hasSavedSizeSelection = true;

          // If selected size doesn't equal the previous selected size, update project in Mediaclip and mark as previous
          if (
            useSizeStore().previousSelectedSizeValue.attributes.sku !==
            useSizeStore().selectedSizeValue.attributes.sku
          ) {
            useMediaclipCommunication().updateProjectOnMobile();
            useSizeStore().previousSelectedSizeOption =
              useSizeStore().selectedSizeOption;
          }

          // Show or hide the most chosen badge based on the selected size
          if (this.isUploadConfigurator) {
            await useSizeStore().handleMostChosenBadgeVisibility();
          }

          return;
        }

        // If user presses "Back" button, reset selected size value to the previous
        useSizeStore().selectedSizeOption =
          useSizeStore().previousSelectedSizeOption;

        // We need to wait a bit because a watcher changes the previous selected size value to the wrong size
        await nextTick();

        // Reset the previous selected size
        useSizeStore().previousSelectedSizeOption =
          useSizeStore().selectedSizeOption;
      },

      resetData(cleanUpStores: boolean = true): void {
        // Clean up local storage
        localStorage.removeItem(useConfiguratorStore().$id);
        localStorage.removeItem(useFilterStore().$id);
        localStorage.removeItem(useExtraOptionsStore().$id);
        localStorage.removeItem(useSizeStore().$id);
        localStorage.removeItem(useMediaclipStore().$id);
        localStorage.removeItem(`product-${window.product.entity_id}-price`);

        // Clean up current stores
        if (cleanUpStores) {
          useConfiguratorStore().$reset();
          useMediaclipStore().$reset();
        }
      },

      resetMediaclipProject(): void {
        this.mediaclipInitialized = false;
        this.mediaclipLaunched = false;
        this.mediaclipToken = undefined;
        this.mediaclipProjectId = undefined;
      },
    },

    persist: {
      paths: [
        'currentStep',
        'mediaclipToken',
        'mediaclipProjectId',
        'mediaclipPreviewUrl',
        'mediaclipUserInputText',
        'identifier',
        'quoteItemId',
        'isEditing',
      ],
    },
  }
);
